import { lazy } from 'react';
const REFRESH_KEY = 'chunk_refresh_attempted';
/**
 * Checks if an error is a chunk load error (failed dynamic import).
 * This typically happens after a new deployment when old chunk files are no longer available.
 */
export function isChunkLoadError(error) {
    if (!(error instanceof Error))
        return false;
    const message = error.message.toLowerCase();
    return (message.includes('failed to fetch dynamically imported module') ||
        message.includes('loading chunk') ||
        message.includes('loading css chunk') ||
        message.includes('cannot find module') ||
        error.name === 'ChunkLoadError');
}
/**
 * Handles chunk load error by refreshing the page once.
 * Uses sessionStorage to prevent infinite refresh loops.
 * Returns true if refresh was triggered, false if already attempted.
 */
export function handleChunkLoadError() {
    const hasRefreshed = sessionStorage.getItem(REFRESH_KEY);
    if (!hasRefreshed) {
        sessionStorage.setItem(REFRESH_KEY, 'true');
        window.location.reload();
        return true;
    }
    return false;
}
/**
 * Clears the chunk refresh flag.
 * Call this on successful page load to allow future refreshes.
 */
export function clearChunkRefreshFlag() {
    sessionStorage.removeItem(REFRESH_KEY);
}
/**
 * A wrapper around React.lazy that automatically handles chunk load errors
 * by refreshing the page once after a new deployment.
 *
 * @example
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry(importFn) {
    return lazy(async () => {
        try {
            const module = await importFn();
            clearChunkRefreshFlag();
            return module;
        }
        catch (error) {
            if (isChunkLoadError(error)) {
                const refreshed = handleChunkLoadError();
                if (refreshed) {
                    // Return a placeholder while the page refreshes
                    return { default: (() => null) };
                }
            }
            throw error;
        }
    });
}
